import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { UtmParamsService } from '@shared/services/utm-params/utm-params.service';
import { FINISH_ROUTES } from '@shipment-record/constants/shipment-record-step.constant';

/**
 * Mantiene los parámetros UTM en la URL durante todo el flujo de registro de envíos.
 *
 * - Rehidrata las UTM desde el almacenamiento de sesión en activaciones frescas
 *   (recargas, retrocesos y el callback externo de la pasarela de pago).
 * - Asegura que la vista final de confirmación conserve las UTM originales.
 * - Al alcanzar una vista final marca la propagación como terminada para que
 *   navegaciones posteriores ya no sigan arrastrando las UTM (CA06).
 */
export const utmPersistenceGuard: CanActivateChildFn = (_childRoute, state) => {
    const utmParamsService = inject(UtmParamsService);
    const router = inject(Router);

    const urlTree = router.parseUrl(state.url);
    const urlUtmParams = utmParamsService.extractUtmParams(urlTree.queryParams);

    if (!utmParamsService.hasUtm()) {
        if (Object.keys(urlUtmParams).length === 0) {
            return true;
        }

        // Ingreso directo al flujo con UTM en la URL: se conservan para el resto del proceso.
        utmParamsService.setUtmParams(urlUtmParams);
        utmParamsService.resetPropagation();
    }

    const isFinishRoute = Object.values(FINISH_ROUTES).some((route) => state.url.startsWith(route));

    if (utmParamsService.isPropagationDone() && !isFinishRoute) {
        return true;
    }

    if (isFinishRoute) {
        utmParamsService.markPropagationDone();
    }

    const utmQueryParams = utmParamsService.getUtmQueryParams();
    const isMissingUtm = Object.entries(utmQueryParams).some(([key, value]) => urlTree.queryParams[key] !== value);

    if (!isMissingUtm) {
        return true;
    }

    Object.entries(utmQueryParams).forEach(([key, value]) => {
        urlTree.queryParams[key] = value;
    });

    return urlTree;
};
